/**
 * DesignPoleNode - Pole (stĺpik) node for network design
 * Represents a pole with expected customers and auto-generated network design
 */
import React, { useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';
import { SplitterType } from '../../types';

export interface SuggestedSplitter {
  id: string;
  type: SplitterType;
  purpose: 'customers' | 'distribution' | 'passthrough';
  inputFiber: number;
  outputFibers: number[];
  loss: number;
  note: string;
}

export interface SuggestedSplice {
  id: string;
  fromCable: string;
  toCable: string;
  fiber: number;
  fiberColor: string;
  loss: number;
}

export interface DesignPoleNodeData {
  label: string;
  expectedCustomers: number;
  
  // Auto-generated network design
  suggestedSplitters?: SuggestedSplitter[];
  suggestedSplices?: SuggestedSplice[];
  suggestedONUs?: number;
  
  // Warnings and recommendations
  warnings?: string[];
  recommendations?: string[];
  
  // Signal info
  inputPower?: number;
  availableFibers?: number;
}

export const DesignPoleNode: React.FC<NodeProps> = ({ id, data: nodeData, selected }) => {
  const data = nodeData as unknown as DesignPoleNodeData;
  const { updateNodeData, deleteElements } = useReactFlow();
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelValue, setLabelValue] = useState(data.label);

  const handleLabelChange = () => {
    updateNodeData(id as string, {
      ...data,
      label: labelValue,
    });
    setIsEditingLabel(false);
  };

  const handleCustomersChange = (value: number) => {
    updateNodeData(id as string, {
      ...data,
      expectedCustomers: value,
    });
  };

  const handleDelete = () => {
    if (window.confirm(`Delete ${data.label}?`)) {
      deleteElements({ nodes: [{ id: id as string }] });
    }
  };

  return (
    <div className={`design-node design-pole-node ${selected ? 'selected' : ''}`}>
      <div className="design-node-header">
        <span>🏗️</span>
        {isEditingLabel ? (
          <input
            type="text"
            value={labelValue}
            onChange={(e) => setLabelValue(e.target.value)}
            onBlur={handleLabelChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleLabelChange();
              if (e.key === 'Escape') {
                setLabelValue(data.label);
                setIsEditingLabel(false);
              }
            }}
            autoFocus
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              outline: 'none',
              width: '120px',
            }}
          />
        ) : (
          <span
            onDoubleClick={() => setIsEditingLabel(true)}
            style={{ cursor: 'text' }}
            title="Dvojklik na úpravu"
          >
            {data.label}
          </span>
        )}
        <button
          onClick={handleDelete}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px',
            padding: '2px 4px',
            marginLeft: 'auto',
          }}
          title="Zmazať stĺpik"
        >
          🗑️
        </button>
      </div>
      
      <div className="design-node-body">
        <div className="design-field">
          <span className="design-field-label">Customers:</span>
          <input
            type="number"
            className="design-field-input"
            value={data.expectedCustomers}
            min="0"
            onChange={(e) => handleCustomersChange(parseInt(e.target.value) || 0)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
        
        {data.inputPower !== undefined && (
          <div className="design-field">
            <span className="design-field-label">Signal:</span>
            <span className="design-field-value">
              {data.inputPower.toFixed(2)} dBm
            </span>
          </div>
        )}
        
        {data.availableFibers !== undefined && (
          <div className="design-field">
            <span className="design-field-label">Fibers:</span>
            <span className="design-field-value">
              {data.availableFibers}/12
            </span>
          </div>
        )}
        
        {data.suggestedONUs !== undefined && data.suggestedONUs > 0 && (
          <div className="design-field">
            <span className="design-field-label">ONUs:</span>
            <span className="design-field-value">
              {data.suggestedONUs}
            </span>
          </div>
        )}
        
        {data.warnings && data.warnings.length > 0 && (
          <div className="design-warning" style={{ marginTop: '8px', fontSize: '10px' }}>
            ⚠️ {data.warnings[0]}
          </div>
        )}

        {data.recommendations && data.recommendations.length > 0 && (
          <div className="design-success" style={{ marginTop: '8px', fontSize: '10px' }}>
            💡 {data.recommendations[0]}
          </div>
        )}
      </div>
      
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        style={{ background: '#9c27b0' }}
      />
      
      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        style={{ background: '#9c27b0' }}
      />
    </div>
  );
};

